// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { handleFooterQuickLinkClick } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface FooterLink {
  label: string;
  link: string;
}

export interface SocialLink {
  platform: string;
  url: string;
  icon: string;
}

/**
 * Modern page footer component used across the website builder to render the site's footer with branding, quick links, and social links.
 * @component
 * @selector app-modern-page-footer
 * @example
 *   <app-modern-page-footer
 *     [logo]="{ text: 'Way', image: '/assets/logo.png' }"
 *     [quickLinks]="links"
 *     [socialLinks]="socials"
 *     [quickLinksTitle]="'Quick links'"
 *     [socialLinksTitle]="'Connect with us'"
 *     [backgroundColor]="'#ffffff'"
 *     [visible]="true">
 *   </app-modern-page-footer>
 * @input {Object} logo - Optional brand object containing text and/or image used in the footer (e.g., { text?: string; image?: string }).
 * @input {FooterLink[]} quickLinks - Array of quick navigation links displayed in the footer; each item has a label and a link (default includes Home, Express Washes, Coupons, Memberships, Express Details, Fleets).
 * @input {string} quickLinksTitle - Title displayed above the quick links section (default: 'Quick links').
 * @input {SocialLink[]} socialLinks - Array of social media links with platform, url and icon properties (default includes Instagram, Facebook, YouTube, LinkedIn, X).
 * @input {string} socialLinksTitle - Title displayed above the social links section (default: 'Connect with us').
 * @input {string} copyright - Copyright or legal text shown in the footer (default: `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`).
 * @input {string} backgroundColor - Optional CSS color used for the footer background.
 * @input {boolean} visible - Controls whether the footer is rendered or hidden (default: true).
 * @output None - This component does not emit output events; it is purely presentational.
 * Key methods: None — the component is a presentational Angular standalone component without additional public methods.
 * Lifecycle hooks: None implemented in this class — there are no ngOnInit/ngOnDestroy hooks defined here.
 */
@Component({
  selector: 'app-modern-page-footer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './footer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./footer.component.scss'],
})
export class ModernPageFooterComponent {
  @Input() logo?: {
    text?: string;
    image?: string;
  };

  @Input() quickLinks: FooterLink[] = [
    { label: 'Home', link: '#home' },
    { label: 'Express Washes', link: '#express-wash-packages' },
    { label: 'Coupons', link: '#car-wash-coupons--way' },
    { label: 'Memberships', link: '#memberships' },
    { label: 'Express Details', link: '#express-auto-details' },
    { label: 'Fleets', link: '#fleet-management' },
  ];

  @Input() quickLinksTitle = 'Quick links';

  @Input() socialLinks: SocialLink[] = [
    { platform: 'Instagram', url: '#', icon: 'instagram' },
    { platform: 'Facebook', url: '#', icon: 'facebook' },
    { platform: 'YouTube', url: '#', icon: 'youtube' },
    { platform: 'LinkedIn', url: '#', icon: 'linkedin' },
    { platform: 'X', url: '#', icon: 'x' },
  ];

  @Input() socialLinksTitle = 'Connect with us';

  @Input() copyright = `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;

  @Input() backgroundColor?: string;

  @Input() visible = true;

  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Output() quickLinkClick = new EventEmitter<string>();

  onQuickLinkClick(event: Event, link: FooterLink): void {
    handleFooterQuickLinkClick(event, link, pageType => this.quickLinkClick.emit(pageType));
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
